import cx from 'classnames';
import { QueryClient, QueryClientProvider as QueryClientProviderRQ } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import type { FC, ReactNode } from 'react';
import { createPortal } from 'react-dom';

import styles from './QueryClientProvider.module.scss';
import { getQueryClient, setQueryClient } from './queryClientSingleton';

if (!getQueryClient()) {
    const queryClient = new QueryClient();
    setQueryClient(queryClient);
}

interface Props {
    children: ReactNode;
    withDevTools?: boolean;
}

/**
 * A wrapper component that provides a QueryClient to its children and optionally includes React Query DevTools.
 *
 * This component uses a singleton QueryClient instance and wraps the children with
 * It conditionally renders the React Query DevTools in development mode.
 *
 * @component
 * @example
 * <QueryClientProvider withDevTools>
 *   <App />
 * </QueryClientProvider>
 *
 * @param {Object} props - The component props.
 * @param {ReactNode} props.children - The child components to be wrapped.
 * @param {boolean} [props.withDevTools=false] - Whether to include the React Query DevTools.
 *
 * @returns {ReactElement} The wrapped children with QueryClient context and optional DevTools.
 *
 * @remarks
 * - The QueryClient is initialized as a singleton if it doesn't already exist.
 * - DevTools are only rendered in development mode (__DEV__) and when `withDevTools` is true.
 * - DevTools are rendered in a portal at the bottom of the document body.
 */
export const QueryClientProvider: FC<Props> = ({ children, withDevTools }) => {
    return (
        <QueryClientProviderRQ client={getQueryClient()}>
            <>
                {__DEV__ &&
                    withDevTools &&
                    createPortal(
                        <div className={cx(styles.devtools, 'd-print-none')}>
                            <ReactQueryDevtools
                                initialIsOpen={false}
                                buttonPosition="bottom-left"
                            />
                        </div>,
                        document.body,
                    )}
                {children}
            </>
        </QueryClientProviderRQ>
    );
};
